import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Button,
    Typography,
    Checkbox,
    Tag,
} from '../../../../../../components';
import { AppstoreOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { CreatableSelect } from '../../../../../../components/CreatableSelect';
import dayjs from 'dayjs';
import '../CreateAndManageModal.scss';
import './EditMoveGroupModal.scss';

const { Option } = Select;
const { Title, Text } = Typography;

/**
 * EditMoveGroupModal - Modal for editing an existing move group
 * Includes options to update group details, assign/unassign applications,
 * and remove group assignment from all machines and apps
 */
export const EditMoveGroupModal = ({ open, onCancel, onApply, moveGroup = null, applications = [], users = [] }) => {
    const [form] = Form.useForm();
    const [removeAssignment, setRemoveAssignment] = useState(false);
    const [assignedApps, setAssignedApps] = useState([]);
    const [removedAppIds, setRemovedAppIds] = useState([]);

    const hasAssignedVMs = moveGroup?.machines > 0 || moveGroup?.vms?.length > 0;
    const hasAssignedApps = moveGroup?.apps > 0 || moveGroup?.applications?.length > 0;
    const hasAnyAssignments = hasAssignedVMs || hasAssignedApps;

    const getUnassignDescription = () => {
        if (hasAssignedVMs && hasAssignedApps) {
            return 'Remove this group assignment from assigned machines and apps.';
        } else if (hasAssignedVMs) {
            return 'Remove this group assignment from assigned machines.';
        } else if (hasAssignedApps) {
            return 'Remove this group assignment from assigned apps.';
        }
        return 'Remove all assignments from this group.';
    };

    useEffect(() => {
        if (open && moveGroup) {
            form.setFieldsValue({
                name: moveGroup.name || '',
                user: moveGroup.assigned_resource,
                targetMigrationDate: moveGroup.target_migration_date ? dayjs(moveGroup.target_migration_date) : null,
                applications: [],
            });

            // Set assigned applications for display as tags
            if (moveGroup.application_ids && moveGroup.application_ids.length > 0) {
                const apps = applications.filter((app) => moveGroup.application_ids.includes(app.value));
                setAssignedApps(apps);
            } else {
                setAssignedApps([]);
            }

            setRemovedAppIds([]);
        }
    }, [open, moveGroup, applications, users, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Combine existing assigned apps (shown as tags) with newly selected apps from the picker
            const existingAppIds = assignedApps.map((app) => app.value);
            const newlySelectedAppIds = values.applications || [];
            const allApplicationIds = [...new Set([...existingAppIds, ...newlySelectedAppIds])];

            const payload = {
                id: moveGroup?.id,
                name: values.name,
                user: values.user,
                targetMigrationDate: values.targetMigrationDate,
                applications: allApplicationIds,
                removedApplications: removedAppIds,
                removeAssignment,
            };

            onApply(payload);
            handleCancel();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setRemoveAssignment(false);
        setAssignedApps([]);
        setRemovedAppIds([]);
        onCancel();
    };

    const handleRemoveAssignmentChange = (e) => {
        setRemoveAssignment(e.target.checked);
        if (e.target.checked) {
            // Clear form fields when removing assignment
            form.setFieldsValue({
                user: undefined,
                targetMigrationDate: null,
                applications: [],
            });
        }
    };

    // Handle changes in the applications select
    const handleApplicationChange = (value) => {
        if (value && value.length > 0) {
            const newlySelectedApps = value.map((val) => {
                const existingApp = applications.find((app) => app.value === val);
                if (existingApp) {
                    return existingApp;
                } else {
                    return { value: val, label: val };
                }
            });

            setAssignedApps((prevApps) => {
                const existingIds = new Set(prevApps.map((app) => app.value));
                const appsToAdd = newlySelectedApps.filter((app) => !existingIds.has(app.value));
                return [...prevApps, ...appsToAdd];
            });

            if (moveGroup.application_ids) {
                const currentAppIds = [...assignedApps.map((app) => app.value), ...value];
                const newRemovals = moveGroup.application_ids.filter(
                    (originalId) => !currentAppIds.includes(originalId),
                );
                setRemovedAppIds(newRemovals);
            }
        }

        form.setFieldsValue({ applications: [] });
    };

    return (
        <Modal
            className="create-manage-modal edit-move-group-modal"
            title={
                <Title level={4} className="modal-title">
                    Edit Move Group
                </Title>
            }
            open={open}
            onCancel={handleCancel}
            footer={null}
            width={500}
            destroyOnHidden
        >
            <Text type="secondary" className="modal-description">
                Edit selected Move Group details.
            </Text>

            <Form form={form} layout="vertical" className="manage-modal-form">
                {/* Move Group Name */}
                <Form.Item
                    label={
                        <span>
                            <FolderOpenOutlined className="icon-with-label" /> Move Group
                        </span>
                    }
                    name="name"
                    rules={[{ required: true, message: 'Move Group name is required' }]}
                >
                    <Input placeholder="Enter Move Group name" disabled={removeAssignment} />
                </Form.Item>

                {/* Unassign Checkbox - Only show if move group has VMs or applications assigned */}
                {hasAnyAssignments && (
                    <div className="remove-assignment-section">
                        <Checkbox checked={removeAssignment} onChange={handleRemoveAssignmentChange}>
                            Unassign (Clear all)
                        </Checkbox>
                        <Text type="secondary" className="remove-assignment-description">
                            {getUnassignDescription()}
                        </Text>
                    </div>
                )}

                {/* Applications Selection */}
                <Form.Item
                    label={
                        <span>
                            <AppstoreOutlined className="icon-with-label" /> Applications
                        </span>
                    }
                    name="applications"
                >
                    <CreatableSelect
                        mode="multiple"
                        selectPlaceholder="Select or create Applications"
                        addItemPlaceholder="Create new Application"
                        options={applications}
                        onChange={handleApplicationChange}
                        disabled={removeAssignment}
                        tagRender={() => null}
                        placement="topLeft"
                    />
                </Form.Item>
                <Text type="secondary" className="assigned-apps-note">
                    Note: Applications can only belong to one move group.
                </Text>

                {/* Assigned Applications Tags - shown when apps are assigned */}
                {assignedApps.length > 0 && !removeAssignment && (
                    <div className="assigned-apps-tags">
                        {assignedApps.map((app) => (
                            <Tag
                                key={app.value}
                                color="grey"
                                closable
                                onClose={() => {
                                    // Remove from assignedApps
                                    setAssignedApps((prevApps) => prevApps.filter((a) => a.value !== app.value));
                                    // Track for unassignment if it was originally assigned
                                    if (moveGroup.application_ids && moveGroup.application_ids.includes(app.value)) {
                                        setRemovedAppIds((prev) => [...prev, app.value]);
                                    }
                                }}
                            >
                                {app.label}
                            </Tag>
                        ))}
                    </div>
                )}

                <Form.Item label="Assigned Resource" name="user">
                    <Select
                        placeholder="Select User"
                        allowClear
                        disabled={removeAssignment}
                        showSearch
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
                        }
                    >
                        {users.map((user) => (
                            <Option key={user.value} value={user.value}>
                                {user.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item label="Target Migration Date" name="targetMigrationDate">
                    <DatePicker className="w-100" placeholder="Select date" disabled={removeAssignment} />
                </Form.Item>

                {/* Footer Buttons */}
                <div className="button-group">
                    <Button shape="round" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button shape="round" type="primary" onClick={handleSubmit}>
                        Apply
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
